import React from 'react';
import styled from 'styled-components';
import { CreditApprovalRound, CreditConditionalItem } from '@indigo-ag/schema';

import CreditExceptionTag from '../../shared/CreditExceptionsAndConditionals/CreditExceptions/CreditExceptionTag';
import { getExceptionText, getConditionalText } from 'shared';
import CreditConditionalTag from '../../shared/CreditExceptionsAndConditionals/CreditConditionals/CreditConditionalTag';
import { Divider as DividerComponent, SmallTitle } from '@indigo-ag/component-module';
import i18next from 'core/i18n';

interface CreditApprovalExceptionsAndConditionalsProps {
  round?: CreditApprovalRound;
  exceptionsAlreadyAdded?: string[];
  conditionalsAlreadyAdded?: string[];
  showHeaders?: boolean;
}

const ItemWrapper = styled.div`
  display: inline-block;
  margin-top: 10px;
  margin-right: 10px;
`;

const Divider = styled(DividerComponent)`
  margin: 1rem 0 0 0;
`;

const getCreditConditionals = (conditionals?: CreditConditionalItem[], conditionalsAlreadyAdded?: string[]) => {
  const alreadyAdded = conditionalsAlreadyAdded ?? [];
  return conditionals && conditionals.length > 0 ? [...conditionals.map(c => c.name)] : alreadyAdded;
};

const Title = ({ children }: { children: React.ReactNode }) => (
  <SmallTitle style={{ margin: '1rem 0 0.5rem 0' }}>{children}</SmallTitle>
);

export const CreditApprovalExceptionsAndConditionals = ({
  round,
  exceptionsAlreadyAdded = [],
  conditionalsAlreadyAdded = [],
  showHeaders = false
}: CreditApprovalExceptionsAndConditionalsProps) => {
  const creditExceptions = (round && round.creditExceptions?.length > 0
    ? [...round.creditExceptions]
    : [...exceptionsAlreadyAdded]
  )
    .slice()
    .sort((a, b) => getExceptionText(a).localeCompare(getExceptionText(b)));

  const creditConditionals = [...getCreditConditionals(round?.creditConditionals, conditionalsAlreadyAdded)]
    .slice()
    .sort((a, b) => getConditionalText(a).localeCompare(getConditionalText(b)));

  const hasConditionals = creditConditionals.length > 0;
  const hasExceptions = creditExceptions.length > 0;
  const hasConditionalsAndExceptions = hasConditionals && hasExceptions;

  return (
    <>
      {showHeaders && hasExceptions && <Title>{i18next.t('exceptionsAndConditionals-dividerTitleExceptions')}</Title>}
      {hasExceptions &&
        creditExceptions.map(e => (
          <ItemWrapper key={`exception-${e}`}>
            <CreditExceptionTag exception={e} flagged={exceptionsAlreadyAdded.includes(e)} />
          </ItemWrapper>
        ))}
      {showHeaders && hasConditionalsAndExceptions && <Divider />}
      {showHeaders && hasConditionals && (
        <Title>{i18next.t('exceptionsAndConditionals-dividerTitleConditionals')}</Title>
      )}
      {hasConditionals &&
        creditConditionals.map(c => (
          <ItemWrapper key={`conditional-${c}`}>
            <CreditConditionalTag conditional={c} flagged={conditionalsAlreadyAdded.includes(c)} />
          </ItemWrapper>
        ))}
    </>
  );
};
